import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { Calendar, ChevronLeft, ChevronRight } from '@untitled-ui/icons-react';
import classNames from 'classnames';
import moment from 'moment';
import { Moment } from 'moment-timezone';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import ReactCalendar from 'react-calendar';
import { useIdle, usePreviousDistinct } from 'react-use';

import { Button, ButtonGroup, IconButton, Spinner } from '@myhealth-raccoon/components';
import { useAsyncData } from '@myhealth-raccoon/services';

import { AvailabilityProps, Timeslot } from '.';
import { TimeslotButton } from './TimeslotButton';
import { useClinicSelector } from 'src/hooks/useAsyncSelector';
import { useBreakpoints } from 'src/hooks/useBreakpoints';
import {
    AutomedDoctorAvailabilityTimeModel,
    AutomedDoctorAvailablilityModel,
    SiteBookingAvailabilityModel,
    availabilityService,
} from 'src/services/availability';
import { innerHtml } from 'src/utils/html';

function isWeekend(date: Moment) {
    const dayOfWeek = date.day();
    return dayOfWeek === 0 || dayOfWeek === 6;
}

function getDaySpan(breakpoint: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'xs') {
    switch (breakpoint) {
        case 'sm':
        case 'xs':
            return 4;
        default:
            return 5;
    }
}

const outputError = (e: Error) => {
    if (e.message === 'No availabilities info were found') {
        return "We're sorry, but we have no appointments available for the selected appointment type.";
    }
    return e.message || e.name;
};

export function AvailabilityCalendar({
    selected,
    onSelectTimeSlot,
    appointmentType,
    clinicId,
    doctorId,
    disabled,
    firstAvailableDate,
    errorElement,
    futureApptWeeks,
    firstAvailableIsLoading,
    allUsers,
}: AvailabilityProps) {
    const daySpan = getDaySpan(useBreakpoints());

    const [startDate, setStartDate] = useState(() => moment().startOf('date'));
    const endDate = useMemo(() => moment(startDate).add(daySpan - 1, 'day'), [startDate, daySpan]);
    const lastAvailableDay = useMemo(
        () => (futureApptWeeks ? moment().add(futureApptWeeks, 'weeks') : moment().add(30, 'days')),
        [futureApptWeeks]
    );

    const { data: site } = useClinicSelector();

    const {
        data: availabilityData,
        invoke: refreshAvailability,
        ...timeslotsRequest
    } = useAsyncData(
        {
            service: availabilityService,
            method: availabilityService.GetBookingTimeslot,
            defaultValue: {},
            suppressToast: true,
            selector: useCallback(
                (data: SiteBookingAvailabilityModel[] | AutomedDoctorAvailablilityModel) => {
                    let obj = {} as Record<string, Timeslot[]>;

                    if (Array.isArray(data)) {
                        obj = data.reduce((obj, { user, timeSlots }) => {
                            if (!timeSlots) {
                                return obj;
                            }
                            for (const [date, slots] of Object.entries(timeSlots)) {
                                const mDate = moment(date);
                                if (
                                    (appointmentType.weekdaysOnly && isWeekend(mDate)) ||
                                    (appointmentType.weekendOnly && !isWeekend(mDate))
                                ) {
                                    continue;
                                }

                                if (obj[date]) {
                                    for (const ts of slots) {
                                        const existingSlot = obj[date].find(({ time }) => time === ts.t);

                                        if (existingSlot) {
                                            existingSlot.slots.push({ userId: user.userId, duration: ts.d, ts });
                                        } else {
                                            obj[date].push({
                                                time: ts.t,
                                                slots: [{ userId: user.userId, duration: ts.d, ts }],
                                            });
                                            obj[date] = obj[date].sort(({ time: a }, { time: b }) =>
                                                moment(a, 'HH:mm').isBefore(moment(b, 'HH:mm')) ? -1 : 1
                                            );
                                        }
                                    }
                                } else {
                                    obj[date] = slots.map((ts) => ({
                                        time: ts.t,
                                        slots: [{ userId: user.userId, duration: ts.d, ts }],
                                    }));
                                }
                            }
                            return obj;
                        }, obj);
                    } else if (data.timeslot) {
                        for (const [date, slots] of Object.entries(data.timeslot)) {
                            const mDate = moment(date);
                            if (
                                (appointmentType.weekdaysOnly && isWeekend(mDate)) ||
                                (appointmentType.weekendOnly && !isWeekend(mDate))
                            ) {
                                continue;
                            }

                            obj[date] = slots.map((ts) => ({
                                time: ts.time,
                                slots: [{ userId: Number(doctorId!), duration: ts.duration, ts }],
                            }));
                        }
                    }
                    return obj;
                },
                [doctorId, appointmentType]
            ),
        },
        clinicId,
        appointmentType.appointmentTypeId,
        doctorId,
        useMemo(
            () => ({
                startDate: startDate.toDate(),
                endDate: endDate.toDate(),
                firstAvailableOrCalendar: false,
            }),
            [startDate, endDate]
        )
    );

    const isIdle = useIdle(60e3, false);
    const wasIdle = usePreviousDistinct(isIdle);
    useEffect(() => {
        if (!isIdle && wasIdle) {
            refreshAvailability();
        }
    }, [isIdle, wasIdle, refreshAvailability]);

    const timeSlotsByDay = useMemo(() => {
        return Array(daySpan)
            .fill(0)
            .map((_, i) => {
                const date = moment(startDate).add(i, 'day');
                if (availabilityData[date.format('YYYY-MM-DD')]) {
                    return { date, times: availabilityData[date.format('YYYY-MM-DD')] };
                } else {
                    return { date, times: [] };
                }
            });
    }, [availabilityData, startDate, daySpan]);

    // NOTE: This code is used to get number of slots per day for Calendar Busy Status
    //      For now, this is commented out until we discuss if we need this.
    // const slotCountByDay :{[key: string]: number}= useMemo(() => {
    //     let obj:{[key: string]: number} = {};
    //     Object.keys(availabilityData).map((day)=>{
    //         obj[day] = 0;
    //         availabilityData[day].map((slot) =>{
    //             obj[day] += slot.slots.length;
    //         });
    //     });
    //     return obj;
    // }, [availabilityData, startDate]);

    const parsedFirstAvailableDate = moment(firstAvailableDate);

    // 1. No restrictions are set, OR
    // 2. It's weekdays-only and today is a weekday, OR
    // 3. It's weekend-only and today is a weekend
    const displayFirstAvailableSkip =
        parsedFirstAvailableDate.isValid() &&
        ((!appointmentType.weekdaysOnly && !appointmentType.weekendOnly) ||
            (appointmentType.weekdaysOnly && !isWeekend(parsedFirstAvailableDate)) ||
            (appointmentType.weekendOnly && isWeekend(parsedFirstAvailableDate)));

    return (
        <>
            <div className="flex flex-row items-center p-4 pt-0">
                <h4 className="text-lg text-gray-900">
                    <strong className="font-semibold">
                        {startDate.isSame(endDate, 'month')
                            ? startDate.format('MMMM')
                            : `${startDate.format('MMM')} - ${endDate.format('MMM')}`}
                    </strong>{' '}
                    {startDate.isSame(endDate, 'year')
                        ? startDate.format('YYYY')
                        : `${startDate.format('YYYY')}/${endDate.format('YYYY')}`}
                </h4>

                <Popover as={Fragment}>
                    <ButtonGroup className="ml-auto shadow-sm">
                        <PopoverButton
                            as={IconButton}
                            icon={Calendar}
                            disabled={disabled}
                            className="data-[open]:text-primary"
                        />
                        <IconButton
                            onClick={() => setStartDate((curr) => moment(curr).subtract(daySpan, 'days'))}
                            disabled={disabled || startDate.isSameOrBefore(moment(), 'date')}
                            icon={ChevronLeft}
                        />
                        <IconButton
                            onClick={() => setStartDate((curr) => moment(curr).add(daySpan, 'days'))}
                            disabled={disabled || endDate.isSameOrAfter(lastAvailableDay, 'date')}
                            icon={ChevronRight}
                        />
                    </ButtonGroup>
                    <PopoverPanel
                        transition
                        anchor="bottom end"
                        className="z-50 rounded-lg border border-gray-100 shadow-xl transition duration-200 ease-in-out [--anchor-gap:1px]"
                    >
                        {({ close }) => (
                            <div className="max-w-xs bg-white px-4 py-4">
                                <ReactCalendar
                                    formatShortWeekday={(_, date) => moment(date).format('dd')}
                                    onChange={(value) => {
                                        let date: Date | null;
                                        if (Array.isArray(value)) {
                                            date = value[0];
                                        } else {
                                            date = value;
                                        }
                                        if (date) {
                                            setStartDate(moment(date));
                                            close();
                                        }
                                    }}
                                    value={startDate.toDate()}
                                    minDate={new Date()}
                                    maxDate={lastAvailableDay?.toDate()}
                                    minDetail="month"
                                    returnValue="start"
                                    // NOTE: This code is used to show Calendar Busy Status
                                    //      For now, this is commented out until we discuss if we need this.
                                    // tileClassName={({ date }) => {
                                    //     const mDate = moment(date);
                                    //     const fDate =mDate.format("YYYY-MM-DD");
                                    //     return classNames("border", {
                                    //         "after:bg-success": slotCountByDay[fDate] >= 5 && mDate.isSameOrAfter(moment(), "date") && mDate.isSameOrBefore(lastAvailableDay, "date"),
                                    //         "after:bg-warning": slotCountByDay[fDate] < 5  && mDate.isSameOrAfter(moment(), "date") && mDate.isSameOrBefore(lastAvailableDay, "date"),
                                    //         "after:bg-error": !slotCountByDay[fDate] && mDate.isSameOrAfter(moment(), "date") && mDate.isSameOrBefore(lastAvailableDay, "date")
                                    //     });
                                    // }}
                                    prevLabel={<ChevronLeft className="m-auto h-5 w-5" />}
                                    nextLabel={<ChevronRight className="m-auto h-5 w-5" />}
                                />
                            </div>
                        )}
                    </PopoverPanel>
                </Popover>
            </div>

            {/* 
        green - available slots and not busy
        red - indicates not available or the centre is closed
        YELLOW - limited slots available 
        */}
            <div className="relative border-t border-gray-200">
                <div className="flex flex-row justify-around border-b border-gray-200 px-4">
                    {timeSlotsByDay.map(({ date, times }) => {
                        return (
                            <div
                                className={classNames(
                                    'font-base relative flex-1 p-2 text-center text-sm text-gray-900',
                                    "even:bg-gray-25 after:absolute after:inset-x-0 after:bottom-0 after:mx-auto after:block after:h-[3px] after:w-4 after:rounded-sm after:content-['']",
                                    {
                                        'after:bg-success': times.length > 4,
                                        'after:bg-error text-gray-400': !timeslotsRequest.isLoading && !times.length,
                                        'after:bg-warning': times.length && times.length <= 4,
                                    }
                                )}
                                key={date.toDate().getTime().toString()}
                            >
                                <strong className="font-black">
                                    {date.calendar(null, {
                                        sameDay: '[Today]',
                                        nextDay: 'dddd',
                                        nextWeek: 'dddd',
                                        lastDay: 'dddd',
                                        lastWeek: 'dddd',
                                        sameElse: 'dddd',
                                    })}
                                </strong>
                                <br />
                                <span>{date.format('D MMM')}</span>
                            </div>
                        );
                    })}
                </div>
                <div className="flex flex-col">
                    <div className="flex min-h-44 flex-col items-center justify-center">
                        {errorElement ? (
                            <div className="space-y-1 p-8 text-center">{errorElement}</div>
                        ) : appointmentType.displayOnly === '1' && appointmentType.displayOnlyMessage ? (
                            <div className="prose p-8 text-center" {...innerHtml(appointmentType.displayOnlyMessage)} />
                        ) : timeslotsRequest.isLoading || firstAvailableIsLoading ? (
                            <div className="space-y-1 p-8 text-center">
                                <Spinner className="flex justify-center" />
                                <p>We're fetching the available appointment times. This may take a moment...</p>
                            </div>
                        ) : timeslotsRequest.error ? (
                            <div className="space-y-1 p-8 text-center">
                                <p>
                                    <strong>{outputError(timeslotsRequest.error)}</strong>
                                </p>
                                <p>
                                    Please contact the centre to make an appointment on{' '}
                                    <a className="text-primary" href={`tel:${site?.phoneNumber}`}>
                                        {site?.phoneNumber}
                                    </a>
                                    .
                                </p>
                            </div>
                        ) : timeSlotsByDay.find(({ times }) => !!times?.length) ? (
                            <div className="animate-fade-in grid w-full flex-1 grid-cols-4 px-4 md:grid-cols-5">
                                {timeSlotsByDay.map(({ date, times }) => {
                                    return (
                                        <div
                                            key={date.toISOString()}
                                            className="even:bg-gray-25 flex flex-col gap-y-2.5 px-1 py-4"
                                        >
                                            {times?.map((ts) => {
                                                const time = moment(`${date.format('YYYY-MM-DD')} ${ts.time}`);
                                                // Only trust slots that actually belong to the selected
                                                // provider filter - never relabel another doctor's slot.
                                                const slots =
                                                    doctorId !== undefined
                                                        ? ts.slots.filter((slot) => slot.userId === Number(doctorId))
                                                        : ts.slots;
                                                if (!slots.length) {
                                                    return null;
                                                }
                                                return (
                                                    <TimeslotButton
                                                        key={time.toDate().toString()}
                                                        onClick={(t) =>
                                                            onSelectTimeSlot({
                                                                date: time.toDate(),
                                                                doctorId: t.userId,
                                                                appointmentTypeId: appointmentType.appointmentTypeId,
                                                                clinicId,
                                                                timeSlot: t.ts as AutomedDoctorAvailabilityTimeModel,
                                                            })
                                                        }
                                                        defaultDuration={Number(appointmentType.standardDurationInMins)}
                                                        disabled={disabled}
                                                        selected={selected}
                                                        time={time}
                                                        ts={{ ...ts, slots }}
                                                        allUsers={allUsers}
                                                        unavailableHtml={
                                                            appointmentType.displayOnly === '1'
                                                                ? appointmentType.displayOnlyMessage
                                                                : undefined
                                                        }
                                                    />
                                                );
                                            })}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : displayFirstAvailableSkip ? (
                            <div className="text-center text-sm text-gray-500">
                                <p className="mb-4 text-gray-400">
                                    There are no appointments available for the selected dates.
                                </p>
                                <Button
                                    onClick={() => {
                                        setStartDate(parsedFirstAvailableDate);
                                    }}
                                    disabled={timeslotsRequest.isLoading || disabled}
                                >
                                    Jump to next available date: {parsedFirstAvailableDate.format('dddd, Do MMM')}
                                </Button>
                            </div>
                        ) : doctorId ? (
                            <div className="p-8 text-center text-sm text-gray-500">
                                <p>
                                    <strong className="font-bold text-gray-800">
                                        There are no appointments available for the selected provider.
                                    </strong>
                                </p>
                                <p>
                                    Please select another provider or contact the centre to make an appointment on{' '}
                                    <a className="text-primary" href={`tel:${site?.phoneNumber}`}>
                                        {site?.phoneNumber}
                                    </a>
                                    .
                                </p>
                            </div>
                        ) : (
                            <div className="p-8 text-center text-sm text-gray-500">
                                <p>
                                    <strong className="font-bold text-gray-800">
                                        We’re sorry, but we have no appointments available for the selected appointment
                                        type.
                                    </strong>
                                </p>
                                <p>
                                    Please contact the centre to make an appointment on{' '}
                                    <a className="text-primary" href={`tel:${site?.phoneNumber}`}>
                                        {site?.phoneNumber}
                                    </a>
                                    .
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
